import React, { useEffect, useRef, useState } from "react";
import classes from "./Activities.module.scss";
import { Button, Table, Space, Modal, Typography } from "antd";
import { PlusOutlined, SyncOutlined } from "@ant-design/icons";
import { ADMIN_GAMES, GET_PROD_OR_DEV } from "../../../constants/api";
import { R_ADD_ACTIVITIES, R_SYNC_ACTIVITIES } from "../../../constants/routes";
import axios from "axios";
import Spinner from "../../../Components/Spinner";
import { getCurrentUser, fetchAuthSession } from "@aws-amplify/auth";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import imageTrash from "../../../assets/images/trash.svg";
import ModalDeleteActivities from "./ModalDeleteActivities/ModalDeleteActivities";
import { R_EDIT_ACTIVITIES } from "../../../constants/routes";
import Header from "../../../Components/Header/Header";
import ButtonActions from "../../../Components/Buttons/ButtonActions/ButtonActions";

const { Title, Paragraph } = Typography;

const Activities = () => {
  const [games, setGames] = useState([]);
  const [isLoadingGames, setIsLoadingGames] = useState(false);
  const isMounted = useRef(true);
  const history = useHistory();
  const location = useLocation();
  const spinner = <Spinner />;

  const [loading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [syncSuccessOpen, setSyncSuccessOpen] = useState(false);

  // Executa ao montar o componente: define que está montado e busca os jogos
  useEffect(() => {
    isMounted.current = true;
    getGames();

    // Ao desmontar, define que não está mais montado
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Mostra o modal de sucesso ao voltar da sincronização com merge concluído
  useEffect(() => {
    if (location.state?.syncMerged) {
      setSyncSuccessOpen(true);
      history.replace({ ...location, state: {} });
    }
  }, []);

  // Abre o modal com os dados do jogo selecionado
  const showModal = (game) => {
    setSelectedGame(game);
    setOpen(true);
  };

  // Inicia o carregamento ao confirmar o modal
  const handleOk = () => {
    setIsLoading(true);
  };

  // Fecha o modal sem confirmar
  const handleCancel = () => {
    setOpen(false);
  };

  //Função para trazer a listagem de jogos
  const getGames = () => {
    setIsLoadingGames(true);
    getCurrentUser()
      .then(async (user) => {
        const session = await fetchAuthSession({
          forceRefresh: true,
        });
        const userToken = session.tokens?.idToken?.toString();
        return axios.get(ADMIN_GAMES, {
          headers: {
            Authorization: userToken,
          },
        });
      })
      .then((response) => {
        setIsLoadingGames(false);

        if (isMounted.current) {
          // console.log("Resposta da API:", response.data);

          const data = response.data;

          const updatedData = data.map((game) => {
            return game;
          });

          if (Object.keys(updatedData).length > 0) {
            setGames(updatedData.sort((a, b) => b.id - a.id));
            localStorage.setItem("games", JSON.stringify(updatedData));
          } else {
            setGames([{ error: "Não foi possível trazer os jogos" }]);
          }
        }
      })
      .catch((error) => {
        setIsLoadingGames(false);
        console.error("Erro ao carregar jogos:", error);
        if (isMounted.current) {
          setGames([]);
        }
      });
  };

  // Função para mapear os nomes das colunas da tabela
  const getHeadTableNames = (name) => {
    const names = {
      id: "Id",
      name: "Nome",
      type: "Tipo",
      language: "Idioma",
      release_date: "Lançamento",
      behavior_tags: "Behavior Tags",
      visible: "Visibilidade",
      favorites: "Favoritos",
      likes: "Curtidas",
    };
    return names[name] || name;
  };

  // Lista de colunas permitidas
  const selectedColumns = [
    "id",
    "name",
    "type",
    "language",
    "release_date",
    "behavior_tags",
    "visible",
    "favorites",
    "likes",
  ];

  // Função para garantir que os dados da tabela sejam únicos
  function uniq_fast(a) {
    var seen = {};
    var out = [];
    var len = a.length;
    var j = 0;
    for (var i = 0; i < len; i++) {
      var item = a[i];
      if (seen[item] !== 1) {
        seen[item] = 1;
        out[j++] = item;
      }
    }
    return out;
  }

  //Estilo para os tipos de jogos
  const typeStyles = {
    jogo: {
      color: "#08979C",
      background: "#E6FFFB",
      border: "1px solid #87E8DE",
    },
    audio: {
      color: "#084C9C",
      background: "#E6F3FF",
      border: "1px solid #87B9E8",
    },
    default: {
      color: "#000",
      background: "#f0f0f0",
      border: "1px solid #ccc",
    },
  };

  //Estilo para a visibilidade dos jogos
  const visibleStyles = {
    false: {
      text: "Oculto",
      color: "#D80404",
    },
    true: {
      text: "Visível",
      color: "#06A820",
    },
  };

  // Gera colunas dinâmicas para a tabela com base nas colunas selecionadas
  const columns = selectedColumns.map((key) => ({
    dataIndex: key,
    title: getHeadTableNames(key),
    key,

    // Habilita ordenação por valor ou data
    sorter: (a, b) => {
      const fieldA = a[key];
      const fieldB = b[key];

      if (!isNaN(fieldA) && !isNaN(fieldB)) {
        return fieldA - fieldB;
      }

      if (key === "release_date") {
        return new Date(fieldA).getTime() - new Date(fieldB).getTime();
      }
      return String(fieldA).localeCompare(String(fieldB));
    },

    // Define o conteúdo exibido na célula
    render: (value) => {
      if (key === "release_date") {
        if (!value) return "/";

        const d = new Date(value);
        const day = String(d.getUTCDate()).padStart(2, "0");
        const month = String(d.getUTCMonth() + 1).padStart(2, "0");
        const year = d.getUTCFullYear();

        return `${day}/${month}/${year}`;
      }
      if (key === "type") {
        const { color, background, border } =
          typeStyles[value] || typeStyles.default;
        return (
          <span
            style={{
              backgroundColor: background,
              color: color,
              border: border,
              borderRadius: "4px",
              padding: "4px 8px",
            }}
          >
            {value}
          </span>
        );
      }
      if (key === "visible") {
        const { text, color } = visibleStyles[value || "false"];
        return (
          <span style={{ color: color, fontSize: "13px", fontWeight: "400" }}>
            {text}
          </span>
        );
      }
      return Array.isArray(value) ? value.join(", ") : value?.toString() || "-";
    },

    // Gera filtros únicos para a coluna
    filters: uniq_fast([
      ...games.reduce((_acc, _row) => {
        if (key === "tags") {
          if (_row[key]) _acc.push(..._row[key].split(","));
        } else {
          _acc.push(_row[key]);
        }
        return _acc;
      }, []),
    ]).map((row) => ({
      text: row,
      value: row,
      style: typeStyles[row] || typeStyles.default,
    })),

    // Permite a busca dentro dos filtros
    filterSearch: true,

    // Define lógica de filtro
    onFilter: (value, record) => {
      return String(record[key])?.includes(value);
    },
  }));

  // Coluna para o botão de editar e deletar
  columns.push({
    title: "Editar",
    dataIndex: "",
    key: "actions",
    render: (_, record) => {
      return (
        <Space>
          <Button
            className={classes.EditActivities}
            onClick={() =>
              history.push({
                pathname: R_EDIT_ACTIVITIES(record.id),
                state: { game: record },
              })
            }
          >
            Editar
          </Button>
          <Button
            className={classes.DeleteButton}
            onClick={() => showModal(record)}
            danger
          >
            <img src={imageTrash} alt="Ícone de lixeira" />
          </Button>
        </Space>
      );
    },
  });

  return (
    <div>
      <div className={classes.ActivitiesList}>
        <Header text="Atividades Afinando" />

        <div className={classes.ActivitiesButtons} style={{ margin: "50px 0" }}>
          <ButtonActions
            variant="primary"
            onClick={() => history.push(R_ADD_ACTIVITIES)}
            text="Nova Atividade"
            icon={<PlusOutlined />}
          />

          {GET_PROD_OR_DEV === "dev" && (
            <ButtonActions
              variant="primary"
              onClick={() => history.push(R_SYNC_ACTIVITIES)}
              text="Sincronizar"
              icon={<SyncOutlined />}
            />
          )}
        </div>
      </div>

      {isLoadingGames ? (
        <Spinner />
      ) : (
        <Table
          className={classes.ActivitiesTable}
          columns={columns}
          dataSource={games.map((game, i) => ({ ...game, key: i }))}
          scroll={{ x: "auto" }}
        />
      )}
      <ModalDeleteActivities
        open={open}
        handleOk={handleOk}
        handleCancel={handleCancel}
        loading={loading}
        game={selectedGame}
      />

      <Modal
        modalRender={(modal) => (
          <div style={{ borderRadius: "14px", overflow: "hidden" }}>
            {modal}
          </div>
        )}
        centered
        open={syncSuccessOpen}
        onCancel={() => setSyncSuccessOpen(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <ButtonActions
              variant="primary"
              onClick={() => setSyncSuccessOpen(false)}
              text="OK"
            />
          </div>
        }
      >
        <Title
          level={4}
          style={{
            color: "#0D6E7A",
            fontSize: "24px",
            fontWeight: "600",
            margin: "15px 0 30px 20px",
          }}
        >
          Sucesso!
        </Title>
        <Paragraph
          style={{
            color: "#4D4D4D",
            fontSize: "16px",
            fontWeight: "400",
            margin: "15px 0 30px 20px",
          }}
        >
          Itens mergeados com sucesso!
        </Paragraph>
      </Modal>
    </div>
  );
};

export default Activities;
